/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { Labels, Pagination } from "baseui/pagination";
import { boundWithinRange } from "../Utils/Common";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";

const PAGE_LABELS: Labels = {
  prevButton: " ",
  nextButton: " ",
  preposition: "of",
};

export interface FileTablePaginationProps {
  /** 1-based page number, owned by the parent */
  currentPage: number;
  numPages: number;
  onPageChange: (page: number) => void;
}

const FileTablePagination = ({
  currentPage,
  numPages,
  onPageChange,
}: FileTablePaginationProps): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const { colors } = currentTheme;

  const handlePageChange = React.useCallback(
    ({ nextPage }: { nextPage: number }) => {
      onPageChange(boundWithinRange(nextPage, 1, numPages));
    },
    [numPages, onPageChange]
  );

  return (
    <div
      style={{
        flexShrink: 0,
        paddingTop: currentTheme.spacing.md,
        paddingBottom: currentTheme.spacing.sm,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Pagination
        currentPage={currentPage}
        numPages={numPages}
        onPageChange={handlePageChange}
        labels={PAGE_LABELS}
        overrides={{
          Root: {
            style: { color: colors.text },
          },
          PrevButton: {
            style: {
              color: colors.text,
              ":hover": { backgroundColor: colors.dropdownItemHoverBackground },
            },
          },
          NextButton: {
            style: {
              color: colors.text,
              ":hover": { backgroundColor: colors.dropdownItemHoverBackground },
            },
          },
          DropdownContainer: {
            style: { color: colors.text },
          },
          Select: {
            props: {
              overrides: {
                ControlContainer: {
                  style: {
                    backgroundColor: "transparent",
                    color: colors.text,
                    borderColor: colors.text,
                    ":hover": {
                      backgroundColor: colors.dropdownItemHoverBackground,
                    },
                  },
                },
                SingleValue: {
                  style: { color: colors.text },
                },
                Dropdown: {
                  style: {
                    backgroundColor: colors.pageBackgroundColor,
                    color: colors.text,
                  },
                },
                DropdownListItem: {
                  style: ({ $isHighlighted }: any) => ({
                    backgroundColor: $isHighlighted
                      ? colors.dropdownItemHoverBackground
                      : colors.pageBackgroundColor,
                    color: colors.text,
                  }),
                },
                Option: {
                  style: {
                    backgroundColor: colors.pageBackgroundColor,
                    color: colors.text,
                    ":hover": {
                      backgroundColor: colors.dropdownItemHoverBackground,
                    },
                  },
                },
                OptionContent: {
                  style: { color: colors.text },
                },
              },
            },
          },
          MaxLabel: {
            style: { color: colors.text },
          },
        }}
      />
    </div>
  );
};

export default FileTablePagination;
